import { View, StyleSheet, ActivityIndicator } from "react-native";
import { useState, useCallback } from "react";
import { useFocusEffect } from "@react-navigation/native";
import AdminLayout from "../../layouts/AdminLayout.web";
import { MotiView } from "moti";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import CourseTabNavigator from "../../components/adminDashboard/CourseTabNavigator/CourseTabNavigator";
import AdminCourseAnalyticsContainer from "../../containers/adminDashboard/AdminCourseAnalyticsContainer/AdminCourseAnalyticsContainer";
import AdminCourseModulesContainer from "../../containers/adminDashboard/AdminCourseModulesContainer/AdminCourseModulesContainer";
import AdminRosterManagementContainer from "../../containers/adminDashboard/AdminRosterManagementContainer/AdminRosterManagementContainer";
import AdminCommunityContainer from "../../containers/adminDashboard/AdminCommunityContainer/AdminCommunityContainer";
import AdminAnnouncementsContainer from "../../containers/adminDashboard/AdminAnnouncementsContainer/AdminAnnouncementsContainer";
import AdminSettingsContainer from "../../containers/adminDashboard/AdminSettingsContainer/AdminSettingsContainer";
import { NavigationProp, useNavigation, useRoute, RouteProp } from "@react-navigation/native";
import type { RootStackParamList } from "../../navigation/navigation.types";
import { useGetCourseAdminStatsQuery, useGetCourseInsightsOverviewQuery } from "@axio/graphql/generated/graphql";
import { EyeIcon, PencilIcon, ClipboardTextIcon, UsersIcon, ArrowLeftIcon, ChartLineIcon } from "@phosphor-icons/react";
import { usePreviewMode } from "../../contexts/PreviewModeContext";
import { CourseInformationTab } from "../../constants/courseInformationTabs";
import { StatCard } from "../../components/adminDashboard/AICompanion/StatCard";
import { colors, radii, spacing, webTransitions } from "../../styles/design-system";

const PreviewIcon = () => <EyeIcon color={colors.grey[700]} size={16} weight="bold" />;
const EditIcon = () => <PencilIcon color="#FFFFFF" size={16} weight="bold" />;

const TAB_DATA = [
  { key: CourseInformationTab.ANALYTICS, title: "Analytics" },
  { key: CourseInformationTab.MODULES, title: "Modules" },
  { key: CourseInformationTab.ROSTER_MANAGEMENT, title: "Roster Management" },
  { key: CourseInformationTab.COMMUNITY, title: "Community" },
  { key: CourseInformationTab.ANNOUNCEMENTS, title: "Announcements" },
  { key: CourseInformationTab.SETTINGS, title: "Settings" },
];

function CourseInformationView() {
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList, "Course Information">>();
  const { enterPreviewMode } = usePreviewMode();

  const courseId = route.params?.courseId;
  const [activeTab, setActiveTab] = useState(CourseInformationTab.ANALYTICS);
  const [isPreviewHovered, setIsPreviewHovered] = useState(false);
  const [isEditorHovered, setIsEditorHovered] = useState(false);

  useFocusEffect(
    useCallback(() => {
      if (route.params?.activeTab) {
        const tabParam = route.params.activeTab as CourseInformationTab;
        if (Object.values(CourseInformationTab).includes(tabParam)) {
          setActiveTab(tabParam);
        }
      }
    }, [route.params?.activeTab])
  );

  const {
    data: courseData,
    loading,
    error,
  } = useGetCourseAdminStatsQuery({
    variables: { id: courseId },
    skip: !courseId,
  });

  const course = courseData?.getCourseAdminStats?.data?.[0];

  const { data: insightsData } = useGetCourseInsightsOverviewQuery({
    variables: { input: { courseId } },
    skip: !courseId,
  });

  if (loading) {
    return (
      <AdminLayout withPadding={false}>
        <View style={styles.pageBackground}>
          <View style={styles.loaderContainer}>
            <ActivityIndicator size="large" color="#0000ff" />
          </View>
        </View>
      </AdminLayout>
    );
  }

  if (error || !course) {
    return (
      <AdminLayout withPadding={false}>
        <View style={styles.pageBackground}>
          <View style={styles.errorContainer}>
            <CustomText text="Course not found" size="l" useTranslationText={false} />
          </View>
        </View>
      </AdminLayout>
    );
  }

  // Calculate stats
  const enrolledStudents = course.enrolledStudents?.length || 0;
  const assignmentCount = course.modules?.reduce((total, module) => total + (module.assignments?.length || 0), 0) || 0;
  const aggregateGradePct = insightsData?.getCourseInsightsOverview?.data?.courseSnapshot?.aggregateGradePct;

  const statCards = [
    {
      icon: <UsersIcon color={colors.blue[700]} size={20} weight="bold" />,
      iconBackgroundColor: colors.blue[100],
      value: enrolledStudents.toLocaleString(),
      label: "ENROLLED STUDENTS",
      color: "info" as const,
    },
    {
      icon: <ClipboardTextIcon color={colors.accent.purple700} size={20} weight="bold" />,
      iconBackgroundColor: colors.accent.purple100,
      value: assignmentCount.toLocaleString(),
      label: "ASSIGNMENTS",
      color: "purple" as const,
    },
    {
      icon: <ChartLineIcon color={colors.success[700]} size={20} weight="bold" />,
      iconBackgroundColor: colors.success[100],
      value: aggregateGradePct != null ? `${Math.round(aggregateGradePct)}%` : "—",
      label: "AVERAGE GRADE",
      color: "success" as const,
    },
  ];

  const handlePreviewCourse = () => {
    // Enter preview mode before navigating
    enterPreviewMode(course.id, course.name);
    navigation.navigate("Custom Course Student", {
      course_id: course.id,
    });
  };

  const handleCourseEditor = () => {
    const courseName = course?.name || "Unknown Course";
    const returnTo = route.params?.returnTo || "Manage Courses";

    navigation.navigate("Custom Coursework Modules", {
      courseId: course.id,
      courseName,
      returnTo,
      parentReturnTo: returnTo,
    });
  };

  const handleBackToTeaching = () => {
    const returnTo = route.params?.returnTo || "Teaching";
    navigation.navigate(returnTo as never);
  };

  // to be used with course code and term/semester. being removed for now 1/9/2026
  // const formatCourseNameWithCode = (courseName: string, courseCode?: string | null): string => {
  //   if (courseCode && courseCode.trim()) {
  //     return `${courseCode} • ${courseName}`;
  //   }
  //   return courseName;
  // };
  const courseStatus = course.isDraft
    ? {
        label: "Draft",
        backgroundColor: colors.warning[50],
        borderColor: colors.warning[200],
        textColor: colors.warning[700],
      }
    : {
        label: "Published",
        backgroundColor: "rgb(243, 254, 231)",
        borderColor: "rgb(166, 239, 103)",
        textColor: "rgb(59, 124, 15)",
      };

  const renderTabContent = () => {
    switch (activeTab) {
      case CourseInformationTab.MODULES:
        return <AdminCourseModulesContainer courseId={courseId} />;
      case CourseInformationTab.ANALYTICS:
        return <AdminCourseAnalyticsContainer courseId={courseId} />;
      case CourseInformationTab.ROSTER_MANAGEMENT:
        return <AdminRosterManagementContainer courseId={courseId} />;
      case CourseInformationTab.COMMUNITY:
        return <AdminCommunityContainer courseId={courseId} />;
      case CourseInformationTab.ANNOUNCEMENTS:
        return <AdminAnnouncementsContainer courseId={courseId} />;
      case CourseInformationTab.SETTINGS:
        return <AdminSettingsContainer courseId={courseId} />;
      default:
        return null;
    }
  };

  return (
    <AdminLayout withPadding={false}>
      <View style={styles.pageBackground}>
        <View style={styles.contentContainer}>
          <MotiView
            from={{
              opacity: 0,
              translateY: 20,
              scale: 1,
            }}
            animate={{
              opacity: 1,
              translateY: 0,
              scale: 1,
            }}
            delay={10}
            // @ts-expect-error - transition is not typed
            transition={{ type: "timing", duration: 500 }}>
            {/* Back Button */}
            <CustomButton
              text="Back"
              styleType="transparent"
              onPress={handleBackToTeaching}
              useTranslationText={false}
              style={styles.backButton}
              textStyle={styles.backButtonText}
              leftIcon={<ArrowLeftIcon size={16} color="#64748B" />}
            />

            <CustomSpacing type="vertical" size="l" />

            {/* Header and Stats Container */}
            <View style={styles.headerStatsContainer}>
              {/* Header Section */}
              <View style={styles.headerContainer}>
                <View style={styles.headerContent}>
                  <View style={styles.titleSection}>
                    <CustomText
                      text={course.name}
                      useTranslationText={false}
                      textType="display"
                      weight="ultraBold"
                      role="heading"
                      aria-level="1"
                      style={styles.courseTitle}
                    />
                    <View
                      style={[
                        styles.statusBadge,
                        {
                          backgroundColor: courseStatus.backgroundColor,
                          borderColor: courseStatus.borderColor,
                        },
                      ]}>
                      <CustomText
                        text={courseStatus.label}
                        size="xs"
                        weight="medium"
                        useTranslationText={false}
                        style={[styles.statusBadgeText, { color: courseStatus.textColor }]}
                      />
                    </View>
                  </View>

                  <CustomText
                    text={course.course_code || ""}
                    size="m"
                    weight="medium"
                    useTranslationText={false}
                    style={styles.courseSubtitle}
                  />
                </View>

                <View style={styles.headerButtons}>
                  <CustomButton
                    text="Preview Course"
                    styleType="lightStroke"
                    size="m"
                    bold={false}
                    leftIcon={<PreviewIcon />}
                    onPress={handlePreviewCourse}
                    onHover={setIsPreviewHovered}
                    useTranslationText={false}
                    style={[
                      styles.headerActionButton,
                      styles.previewButton,
                      isPreviewHovered && styles.headerActionButtonHovered,
                      isPreviewHovered && styles.previewButtonHovered,
                    ]}
                    textStyle={styles.previewButtonText}
                  />
                  <CustomButton
                    text="Course Editor"
                    styleType="primary"
                    size="m"
                    bold={false}
                    leftIcon={<EditIcon />}
                    onPress={handleCourseEditor}
                    onHover={setIsEditorHovered}
                    useTranslationText={false}
                    style={[
                      styles.headerActionButton,
                      styles.editorButton,
                      isEditorHovered && styles.headerActionButtonHovered,
                      isEditorHovered && styles.editorButtonHovered,
                    ]}
                    textStyle={styles.editorButtonText}
                  />
                </View>
              </View>

              <CustomSpacing type="vertical" size="xl" />

              {/* Stats Cards */}
              <View style={styles.statsContainer}>
                {statCards.map((statCard) => (
                  <StatCard
                    key={statCard.label}
                    variant="courseInfo"
                    icon={statCard.icon}
                    iconBackgroundColor={statCard.iconBackgroundColor}
                    value={statCard.value}
                    label={statCard.label}
                    color={statCard.color}
                  />
                ))}

                {/* hidding until we can properly calculate */}
                {/* <View style={styles.statCard}>
                  <View style={[styles.statIcon, styles.gradeIcon]}>
                    <GradeIcon />
                  </View>
                  <View style={styles.statContent}>
                    <CustomText
                      text={`${averageGrade}%`}
                      size="xxl"
                      weight="ultraBold"
                      useTranslationText={false}
                      style={styles.statNumber}
                    />
                    <CustomText
                      text="AVERAGE GRADE"
                      size="xs"
                      weight="medium"
                      useTranslationText={false}
                      style={styles.statLabel}
                    />
                  </View>
                </View> */}
              </View>
            </View>

            <CustomSpacing type="vertical" size="xl" />

            {/* Tab Navigator */}
            <View style={styles.tabContainer}>
              <CourseTabNavigator
                tabData={TAB_DATA}
                activeTab={activeTab}
                onTabPress={(tabKey: string) => setActiveTab(tabKey as CourseInformationTab)}
              />

              {renderTabContent()}
            </View>
          </MotiView>
        </View>
      </View>
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  pageBackground: {
    flex: 1,
    backgroundColor: "#F2F4F7",
    // @ts-expect-error - web only
    minHeight: "100vh",
  },
  contentContainer: {
    padding: 24,
    flex: 1,
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  backButton: {
    alignSelf: "flex-start",
    paddingHorizontal: 0,
  },
  backButtonText: {
    color: "#64748B",
    fontSize: 16,
  },
  headerStatsContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: radii.xl,
    padding: 24,
    marginHorizontal: 0,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowRadius: 3,
    shadowOpacity: 0.1,
  },
  headerContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  headerContent: {
    flex: 1,
  },
  titleSection: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  courseTitle: {
    color: "#1e293b",
    fontSize: 28,
  },
  statusBadge: {
    alignSelf: "center",
    justifyContent: "center",
    borderRadius: radii.full,
    borderWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 1,
    ...webTransitions.fast,
  },
  statusBadgeText: {
    fontSize: 12,
    lineHeight: 14,
    fontWeight: "500",
    letterSpacing: 0.2,
    paddingVertical: 4,
  },
  courseSubtitle: {
    color: "#64748b",
    marginTop: 4,
  },
  headerButtons: {
    flexDirection: "row",
    gap: 12,
  },
  headerActionButton: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: radii.xs,
    ...webTransitions.fast,
  },
  headerActionButtonHovered: {
    transform: [{ scale: 1.015 }],
    shadowOpacity: 0.08,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
    elevation: 6,
  },
  previewButton: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: "rgba(0, 0, 0, 0.1)",
  },
  previewButtonHovered: {
    backgroundColor: colors.grey[50],
  },
  previewButtonText: {
    color: colors.grey[700],
    fontSize: 14,
    lineHeight: 20,
    fontWeight: "500",
  },
  editorButton: {
    backgroundColor: colors.brand[500],
  },
  editorButtonHovered: {
    backgroundColor: colors.brand[600],
  },
  editorButtonText: {
    color: colors.white,
    fontSize: 14,
    lineHeight: 20,
    fontWeight: "500",
  },
  statsContainer: {
    flexDirection: "row",
    gap: 24,
    flexWrap: "wrap",
  },
  tabContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: radii.xl,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    overflow: "hidden",
  },
});

export default CourseInformationView;
